import {
  ContentModule,
  useMotionOptions,
  applyTween,
  useShowInstructions,
} from '@backstage-components/base';
import {css} from '@emotion/react';
import styled from '@emotion/styled';
import {isAfter, isBefore, parseISO} from 'date-fns';
import {useSubscription} from 'observable-hooks';
import {Fragment, FC, useMemo, useEffect, useState} from 'react';
import {
  SchemaType,
  Dividers,
  DefaultUnitLabels,
  UnitLabels,
  instructions,
} from './CountdownDefinition';
import {useMachine} from '@xstate/react';
import {createCountdownStatusMachine} from './status-machine';
import {Box, Flex, Text} from '@chakra-ui/react';
import {motion} from 'framer-motion';

export type Schema<Key extends keyof SchemaType> = Required<
  Pick<SchemaType, Key>
>;

export type CountdownComponentDefinition = ContentModule<
  'Countdown',
  SchemaType
>;

export const CountdownComponent: FC<CountdownComponentDefinition> = (
  definition
) => {
  const {props, id} = definition;
  const {animationStates} = props;
  const [activeVariant, setActiveVariant] = useState<string | undefined>(
    undefined
  );
  const motionOptions = useMotionOptions(animationStates);
  const {observable, broadcast} = useShowInstructions(instructions, definition);
  const machine = useMemo(
    () =>
      createCountdownStatusMachine({
        broadcast,
        endTime: parseISO(props.endTime),
        timezoneOffset: new Date().getTimezoneOffset(),
      }),
    // Intentionally ignoring props.endTime since useMachine will not update
    // state based on the new value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [broadcast]
  );
  const [state, dispatch] = useMachine(machine);

  useEffect(() => {
    dispatch({
      type: 'Countdown:setTargetTime',
      meta: {targetTime: props.endTime},
    });
  }, [dispatch, props.endTime]);

  useSubscription(observable, (instruction) => {
    dispatch(instruction);
    if (instruction.type === 'Countdown:animationState') {
      applyTween(instruction, animationStates, setActiveVariant);
    }
  });
  useEffect(() => {
    // This code is mostly to advance through the state machine in countdown-status-machine.ts
    const now = new Date();
    if (isBefore(now, state.context.endTime) && state.matches('INITIAL')) {
      broadcast({type: 'Countdown:ready', meta: {}});
    } else if (
      isAfter(now, state.context.endTime) &&
      state.matches('INITIAL')
    ) {
      broadcast({type: 'Countdown:complete', meta: {}});
    }
  }, [broadcast, state]);

  const {
    typography = {},
    divider,
    isUnitUnderNum = true,
    isCountdownColumn = false,
    titleText,
    unitLabels = {},
  } = props;

  const hasDaysLeft: boolean = useMemo(
    () => (state.context.remaining.days ?? 0) > 0,
    [state.context.remaining.days]
  );

  const getUnitLabel = (typedUnit: keyof UnitLabels): string => {
    return unitLabels[typedUnit] ?? DefaultUnitLabels[typedUnit];
  };

  const baseUnitProps: CountdownUnitPropsBase = {
    divider,
    isUnitUnderNum,
    typography,
  };

  const styles = [
    css({
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      color: typography.primaryTextColor,
      fontFamily: typography.fontFamily,
    }),
    css`
      ${definition.style}
      ${definition.props.styleAttr}
    `,
  ];

  return (
    <motion.div
      id={id}
      css={styles}
      {...motionOptions}
      animate={activeVariant}
      className="countdown-wrapper"
    >
      <Text
        className="countdown-eyebrow"
        fontFamily={typography.fontFamily}
        fontSize={typography.titleFontSize}
        letterSpacing="0.1em"
        paddingBottom="1rem"
        lineHeight="1.25rem"
      >
        {titleText}
      </Text>
      <Box
        className="countdown-clock"
        display="flex"
        flexDirection={isCountdownColumn ? 'column' : 'row'}
        alignItems={isCountdownColumn ? 'center' : 'flex-start'}
        justifyContent={isCountdownColumn ? 'center' : 'flex-start'}
        fontWeight="100"
      >
        {hasDaysLeft ? (
          <CountdownUnit
            {...baseUnitProps}
            timeValue={state.context.remaining.days}
            typedUnit="days"
            unitLabel={getUnitLabel('days')}
            withDivider
          />
        ) : null}
        <CountdownUnit
          {...baseUnitProps}
          timeValue={state.context.remaining.hours}
          typedUnit="hours"
          unitLabel={getUnitLabel('hours')}
          withDivider
        />
        <CountdownUnit
          {...baseUnitProps}
          timeValue={state.context.remaining.minutes}
          typedUnit="minutes"
          unitLabel={getUnitLabel('minutes')}
          withDivider={!hasDaysLeft}
        />
        {hasDaysLeft ? null : (
          <CountdownUnit
            {...baseUnitProps}
            timeValue={state.context.remaining.seconds}
            typedUnit="seconds"
            unitLabel={getUnitLabel('seconds')}
            withDivider={false}
          />
        )}
      </Box>
    </motion.div>
  );
};

const Numeral = styled.span`
  display: inline-block;
  width: 1ch;
  text-align: center;
`;

const CountdownUnit: FC<CountdownUnitProps> = (props) => {
  const {typography, isUnitUnderNum, timeValue, typedUnit, unitLabel} = props;
  const displayValue = `${timeValue ?? 0}`.padStart(2, '0');
  const numerals = displayValue.split('');
  return (
    <Fragment key={typedUnit}>
      <Box
        className={`countdown-${typedUnit}-wrapper`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection={isUnitUnderNum ? `column` : `row`}
      >
        <Text
          fontWeight="100"
          fontFamily={typography.fontFamily}
          lineHeight="1"
          className="number"
          margin={`0 ${isUnitUnderNum ? 0 : '0.625'}rem ${
            isUnitUnderNum ? '0.625' : 0
          }rem 0`}
          fontSize={typography.clockFontSize}
          color={typography.primaryTextColor}
          minWidth={`${numerals.length}ch`}
          textAlign="center"
          whiteSpace="nowrap"
        >
          {numerals.map((numeral, i) => (
            <Numeral key={i}>{numeral}</Numeral>
          ))}
        </Text>
        <Text
          fontWeight="500"
          className="unit"
          fontSize={typography.unitFontSize}
          fontFamily={typography.fontFamily}
          color={typography.primaryTextColor}
        >
          {unitLabel}
        </Text>
      </Box>
      {props.withDivider ? (
        <Divider
          divider={props.divider}
          typography={{
            ...typography,
            primaryTextColor: 'inherit',
          }}
        />
      ) : null}
    </Fragment>
  );
};

type CountdownUnitPropsBase = Schema<
  'divider' | 'isUnitUnderNum' | 'typography'
>;

interface CountdownUnitProps extends CountdownUnitPropsBase {
  typedUnit: keyof UnitLabels;
  timeValue?: number;
  unitLabel: string;
  withDivider?: boolean;
}

const Divider: FC<Schema<'divider' | 'typography'>> = ({
  divider,
  typography,
}) => {
  return divider !== Dividers.NONE ? (
    <Flex
      className="countdown-divider"
      fontFamily={typography.fontFamily}
      fontSize={typography.clockFontSize}
      color={typography.primaryTextColor}
      margin="0 1rem"
      lineHeight="1"
    >
      {divider}
    </Flex>
  ) : (
    <Flex
      className="countdown-divider-none"
      fontSize={typography.clockFontSize}
      marginRight="1rem"
      lineHeight="1"
    />
  );
};
